import PolicyLayout from "@/components/site/PolicyLayout";
import Seo from "@/components/site/Seo";
import { Button } from "@/components/ui/button";
import { BUSINESS } from "@/lib/siteData";

const COOKIE_TABLE = [
  {
    cat: "Strictly necessary",
    examples: "Session ID, CSRF token, Cookie consent record",
    purpose: "Required for the site to load, secure form submissions and remember your cookie choices.",
    duration: "Session — 12 months",
  },
  {
    cat: "Analytics",
    examples: "Google Analytics _ga, _gid, _gat",
    purpose: "Measure pages viewed, session length and entry/exit pages to improve the site.",
    duration: "Up to 24 months",
  },
  {
    cat: "Marketing",
    examples: "Google Ads conversion, Trustpilot widget",
    purpose: "Measure ad performance and remarket to past visitors. Set only with your consent.",
    duration: "Up to 24 months",
  },
  {
    cat: "Preferences",
    examples: "Theme, last-read article, language",
    purpose: "Remember your preferences across visits.",
    duration: "Up to 12 months",
  },
];

function openCookiePrefs() {
  window.dispatchEvent(new CustomEvent("rp:open-cookie-preferences"));
}

export default function Cookies() {
  return (
    <PolicyLayout title="Cookie Policy" lastUpdated="January 2026" testId="cookies-page">
      <Seo
        title="Cookie Policy"
        description="What cookies Repair Point uses, why we use them, how long they last and how to manage your consent under GDPR."
        path="/cookies"
      />

      <h2 className="font-heading text-xl font-semibold">1. What are cookies?</h2>
      <p>
        Cookies are small text files placed on your device when you visit a website. They are widely
        used to make websites work, work more efficiently and provide information to the site owner.
      </p>

      <h2 className="font-heading text-xl font-semibold">2. Our approach</h2>
      <p>
        We follow the principles of the EU General Data Protection Regulation (GDPR) and the
        ePrivacy Directive: cookies that are not strictly necessary are <strong>opt-in only</strong>.
        We never assume consent from dismissal or continued browsing — you must make an active choice
        in our cookie banner. You can change your choices at any time using the button below.
      </p>

      <div className="my-6">
        <Button
          data-testid="open-cookie-prefs"
          onClick={openCookiePrefs}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          Open cookie preferences
        </Button>
      </div>

      <h2 className="font-heading text-xl font-semibold">3. Cookies we use</h2>
      <div className="not-prose overflow-x-auto border border-border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-foreground/80">
            <tr>
              <th className="text-left font-semibold p-3">Category</th>
              <th className="text-left font-semibold p-3">Examples</th>
              <th className="text-left font-semibold p-3">Purpose</th>
              <th className="text-left font-semibold p-3">Duration</th>
            </tr>
          </thead>
          <tbody>
            {COOKIE_TABLE.map((r) => (
              <tr key={r.cat} className="border-t border-border align-top">
                <td className="p-3 font-medium">{r.cat}</td>
                <td className="p-3 text-muted-foreground">{r.examples}</td>
                <td className="p-3 text-muted-foreground">{r.purpose}</td>
                <td className="p-3 text-muted-foreground whitespace-nowrap">{r.duration}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-heading text-xl font-semibold">4. Your rights under GDPR</h2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li><strong>Right to be informed</strong> — this page explains what we set and why.</li>
        <li><strong>Right to consent</strong> — non-essential cookies require your active opt-in.</li>
        <li><strong>Right to withdraw consent</strong> — re-open the preferences any time via the button above or the footer link.</li>
        <li><strong>Right of access</strong> — write to {BUSINESS.email} for a copy of any personal data we hold.</li>
        <li><strong>Right to erasure</strong> — request deletion of your personal data; we respond within 30 days.</li>
      </ul>

      <h2 className="font-heading text-xl font-semibold">5. Browser-level controls</h2>
      <p>
        You can also manage cookies directly in your browser settings. Most browsers let you block
        third-party cookies, delete existing cookies or set the browser to ask before storing any
        cookie. Note that blocking strictly necessary cookies may prevent parts of the site from
        functioning correctly.
      </p>

      <h2 className="font-heading text-xl font-semibold">6. Changes to this policy</h2>
      <p>
        If we add or change cookies materially, we will re-prompt you for consent and update the
        &ldquo;last updated&rdquo; date at the top of this page.
      </p>

      <h2 className="font-heading text-xl font-semibold">7. Contact</h2>
      <p>
        Questions about this Cookie Policy? Email {BUSINESS.email} or call {BUSINESS.phone}.
      </p>
    </PolicyLayout>
  );
}
